import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { FaLinkedin, FaInstagram, FaGithub } from "react-icons/fa";
import { MdEmail } from "react-icons/md";
import { client } from "../lib/sanity";
import { Link } from "react-router-dom";
import FadeInSection from "../components/FadeInSection";
import HomeWorkHighlight from "../components/HomeWorkHighlight";
import HomeAbout from "../components/HomeAbout";
import HomeHero from "../components/HomeHero";
import Marquee from "../components/Marquee";
import PhotoGallery from "../components/PhotoGallery";

const queryBlogs = `*[_type == "blogPost"] | order(date desc)[0...3]{
  _id,
  title,
  slug { current },
  date,
  excerpt,
  coverImage{ asset->{url} }
}`;

const queryNews = `*[_type == "mediaFeature"] | order(date desc)[0...3]{
  _id,
  title,
  date,
  source,
  summary,
  link,
  coverImage{ asset->{url} }
}`;

export default function Home() {
  const [blogs, setBlogs] = useState([]);
  const [news, setNews] = useState([]);

  useEffect(() => {
    client.fetch(queryBlogs).then(setBlogs);
    client.fetch(queryNews).then(setNews);
  }, []);

  return (
    <>
      <title>Jim Montour | PR Specialist, Fractional Consultant & Development Leader</title>
      <meta
        name="description"
        content="Jim Montour builds communications infrastructure for organizations that need more than messaging — PR strategy, fractional consulting, and development leadership."
      />
      <meta
        property="og:title"
        content="Jim Montour | PR Specialist, Fractional Consultant & Development Leader"
      />
      <meta
        property="og:description"
        content="PR strategy, fractional consulting, and development leadership for mission-driven organizations."
      />
      <link rel="canonical" href="https://jimmontour.com/" />

      {/* Hero is full-bleed — outside the max-width Page wrapper */}
      <HomeHero />
      <Marquee />

      <Page>
        <FadeInSection>
          <HomeAbout />
        </FadeInSection>
        <FadeInSection>
          <PhotoGallery />
        </FadeInSection>
        <FadeInSection>
          <HomeWorkHighlight />
        </FadeInSection>

        <FadeInSection>
          <SectionWrapper>
            <SectionHeading>Latest Blog Posts</SectionHeading>
            <BlogGrid>
              {blogs.map((post) => (
                <BlogCard key={post._id} to={`/blog/${post.slug.current}`}>
                  {post.coverImage?.asset?.url && (
                    <img src={post.coverImage.asset.url} alt={post.title} />
                  )}
                  <div>
                    <h3>{post.title}</h3>
                    <p className="date">
                      {new Date(post.date).toLocaleDateString(undefined, {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </p>
                    <p>{post.excerpt}</p>
                    <span className="read-link">Read More →</span>
                  </div>
                </BlogCard>
              ))}
            </BlogGrid>
            <CenteredLink to="/blog" className="btn-outline">
              View All Articles
            </CenteredLink>
          </SectionWrapper>
        </FadeInSection>

        <FadeInSection>
          <SectionWrapper $alt>
            <SectionHeading>In the News</SectionHeading>
            <BlogGrid>
              {news.map((item) => (
                <BlogCard
                  as="a"
                  key={item._id}
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {item.coverImage?.asset?.url && (
                    <img src={item.coverImage.asset.url} alt={item.title} />
                  )}
                  <div>
                    <h3>{item.title}</h3>
                    <p className="date">
                      {new Date(item.date).toLocaleDateString(undefined, {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </p>
                    <p className="text-sm text-gray-500 mb-1">{item.source}</p>
                    <NewsFade>{item.summary}</NewsFade>
                    <span className="read-link">Read Article →</span>
                  </div>
                </BlogCard>
              ))}
            </BlogGrid>
            <CenteredLink to="/media" className="btn-outline">
              View All Media Features
            </CenteredLink>
          </SectionWrapper>
        </FadeInSection>

        <FadeInSection>
          <SectionWrapper $alt>
            <SectionHeading>Connect With Me</SectionHeading>
            <p
              style={{
                textAlign: "center",
                color: "#374151",
                fontSize: "1.05rem",
              }}
            >
              I'm always open to meaningful conversations, consulting
              engagements, or just saying hi.
            </p>
            <SocialLinks>
              <SocialLink
                href="https://www.linkedin.com/in/jimmontour"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FaLinkedin /> LinkedIn
              </SocialLink>
              <SocialLink
                href="https://www.instagram.com/jimmontour_/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FaInstagram /> Instagram
              </SocialLink>
              <SocialLink
                href="https://github.com/jimmontour"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FaGithub /> GitHub
              </SocialLink>
              <SocialLink
                href="mailto:jimmontour@gmail.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                <MdEmail /> Email
              </SocialLink>
            </SocialLinks>
          </SectionWrapper>
        </FadeInSection>
      </Page>
    </>
  );
}

// ── Styled Components ────────────────────────────────

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 80px;
  max-width: 1100px;
  overflow-x: hidden;
  margin: auto;
  padding: 60px 20px 40px;
`;

const BlogGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 1rem;
`;

const BlogCard = styled(Link)`
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: 0.75rem;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: 1px solid #f3f4f6;
  text-decoration: none;
  color: inherit;
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.1);
    border-color: #e8d5aa;
  }

  img {
    width: 100%;
    height: 180px;
    object-fit: cover;
    display: block;
    transition: transform 0.5s ease;
  }

  &:hover img {
    transform: scale(1.05);
  }

  h3 {
    font-size: 1.05rem;
    margin: 0.75rem 1rem 0;
    color: #111827;
    line-height: 1.35;
  }

  .date {
    font-size: 0.8rem;
    color: #9ca3af;
    margin: 0.3rem 1rem;
    font-family: 'Inter', sans-serif;
  }

  p {
    margin: 0.25rem 1rem 0.75rem;
    font-size: 0.875rem;
    color: #4b5563;
    line-height: 1.65;
  }

  .read-link {
    display: block;
    margin: auto 1rem 1rem;
    font-weight: 600;
    color: #C4A25D;
    font-size: 0.825rem;
    font-family: 'Inter', sans-serif;
  }
`;

const SectionWrapper = styled.section`
  padding: 20px;
  background: ${({ $alt }) => ($alt ? "#f9fafb" : "#ffffff")};
`;

const SectionHeading = styled.h3`
  font-size: 1.75rem;
  color: #1f2937;
  margin-bottom: 1rem;
  position: relative;
  padding-bottom: 0.5rem;
  &::after {
    content: "";
    position: absolute;
    left: 0;
    bottom: 0;
    height: 4px;
    width: 50px;
    background: #C4A25D;
    border-radius: 2px;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: center;
  margin-top: 1.5rem;
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1.25rem;
  border-radius: 999px;
  background: #111111;
  color: #E8D5AA;
  font-size: 1rem;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.2s ease;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);

  svg {
    font-size: 1.25rem;
  }

  &:hover {
    background: #C4A25D;
    color: #111111;
    transform: translateY(-2px);
  }
`;

const CenteredLink = styled(Link)`
  display: block;
  text-align: center;
  margin-top: 2rem;
  font-weight: 600;
`;

const NewsFade = styled.p`
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 0.75rem;
`;
